/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';
import LazyLoad from 'react-lazyload';

import Imgix from 'js/components/Imgix';
import { isRightToLeft } from 'js/lib/language';

import { breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';

import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import { useXdpSubscriptionsForwardDiscovery } from 'bundles/unified-description-page-common/providers/SubscriptionsForwardDiscoveryDataProvider';
import {
  ALTERNATE_UPSELL_CARD_IMAGE_HEIGHT,
  DEFAULT_UPSELL_CARD_IMAGE_HEIGHT,
} from 'bundles/unified-description-page-common/utils/useSubscriptionFirstExpUtils';

import _t from 'i18n!nls/xdp';

type Props = {
  logo?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  cta?: React.ReactNode;
  backgroundImage?: string;
  backgroundPosition?: string;
  backgroundColor?: string;
  amountOfCards?: number;
  wrapperContainerStyle?: SerializedStyles;
  backgroundImageContainerStyle?: SerializedStyles;
  imageHeight?: number;
  containerHeight?: number;
};

const styles = {
  wrapper: (height: number) => css`
    width: 100%;
    min-height: ${height + 'px'};
    border-radius: 8px;
    position: relative;
    display: inline-block;
    padding: var(--cds-spacing-400);
    contain: content;
  `,
  backgroundContainer: (backgroundPosition?: string) => css`
    position: absolute;
    margin: calc(var(--cds-spacing-400) * -1);
    height: 100%;
    width: 100%;
    z-index: -1;

    .lazyload-wrapper {
      position: absolute;
      bottom: 0;
    }

    img {
      max-width: 100% !important; /* Required to override Imgix styling */
      border-radius: var(--cds-border-radius-100);
      object-fit: cover;
      object-position: ${backgroundPosition || `bottom right`};
    }
  `,
  logo: (amountOfCards?: number) => css`
    padding-bottom: var(--cds-spacing-400);
    width: 100px;
    height: 10px;
    padding-top: ${amountOfCards === 1 ? 'var(--cds-spacing-600)' : ''};
    ${breakpoints.down('xs')} {
      padding-top: 0;
    }
  `,
  title: (logo?: string) => css`
    padding-bottom: var(--cds-spacing-200);
    padding-top: ${logo ? '0px' : 'var(--cds-spacing-400)'};
  `,
  link: css`
    padding-top: var(--cds-spacing-400);
  `,
  description: css`
    padding-bottom: var(--cds-spacing-200);
    max-width: 510px;
  `,
  mobileSubFirstWrapper: (backgroundColor?: string) => css`
    padding: var(--cds-spacing-300);
    background-color: ${backgroundColor || 'transparent'};

    ${breakpoints.down('md')} {
      img {
        object-position: 100% !important;
        object-fit: contain !important;
      }
    }
  `,
  mobileSubFirstBackgroundContainer: (isRTL?: boolean) => css`
    margin: calc(var(--cds-spacing-300) * -1);

    img {
      transform: ${isRTL ? 'scale(-1, 1)' : 'none'};
    }
  `,
  mobileSubFirstTitle: css`
    padding-top: 0;

    h2 {
      ${typography2.subtitleLarge};
    }

    ${breakpoints.down('sm')} {
      h2 {
        max-width: 70%;
        padding-bottom: var(--cds-spacing-300);
      }
    }

    ${breakpoints.down(338)} {
      h2 {
        max-width: 75%;
        font: ${typography2.subtitleMedium.font};
      }
    }
  `,
};

const getCardQuality = (amountOfCards?: number) => {
  switch (amountOfCards) {
    case 3:
      return '30';
    case 2:
      return '50';
    case 1:
      return '80';
    default:
      return '50';
  }
};

const UpsellCard = React.forwardRef(
  (
    {
      logo,
      title,
      description,
      cta,
      backgroundImage,
      backgroundPosition,
      backgroundColor,
      amountOfCards,
      wrapperContainerStyle,
      backgroundImageContainerStyle,
      imageHeight,
      containerHeight,
    }: Props,
    ref?: React.Ref<HTMLDivElement>
  ) => {
    const isRTL = isRightToLeft(_t.getLocale());
    const { shouldShowXdpChanges } = useXdpSubscriptionsForwardDiscovery();
    const isMobileSubscriptionFirst = useMediaQuery(breakpoints.down('sm')) && shouldShowXdpChanges;
    const imgWidth = isMobileSubscriptionFirst ? 976 : 1344;
    let imgHeight =
      imageHeight ?? (amountOfCards === 1 ? ALTERNATE_UPSELL_CARD_IMAGE_HEIGHT : DEFAULT_UPSELL_CARD_IMAGE_HEIGHT);

    if (isMobileSubscriptionFirst) {
      imgHeight = 190;
    }

    if (!backgroundImage || !title) {
      return null;
    }

    return (
      <div
        css={[
          styles.wrapper(containerHeight ?? imgHeight),
          isMobileSubscriptionFirst ? styles.mobileSubFirstWrapper(backgroundColor) : '',
          wrapperContainerStyle,
        ]}
        ref={ref}
      >
        <div
          css={[
            styles.backgroundContainer(backgroundPosition),
            isMobileSubscriptionFirst ? styles.mobileSubFirstBackgroundContainer(isRTL) : '',
            backgroundImageContainerStyle,
          ]}
        >
          <LazyLoad offset={100} once>
            <Imgix
              src={backgroundImage}
              alt="" // No alt text required; used only for visual appeal
              width={imgWidth}
              height={imgHeight}
              maxWidth={imgWidth}
              maxHeight={imgHeight}
              imgParams={{ quality: getCardQuality(amountOfCards) }}
            />
          </LazyLoad>
        </div>
        {logo && (
          <div css={styles.logo(amountOfCards)}>
            <LazyImgix src={logo} alt={_t('Coursera Plus')} maxWidth={120} maxHeight={15} />
          </div>
        )}
        <div css={[isMobileSubscriptionFirst ? styles.mobileSubFirstTitle : styles.title(logo)]}>{title}</div>
        {description && !isMobileSubscriptionFirst && <div css={styles.description}>{description}</div>}
        <div css={amountOfCards === 1 && styles.link}>{cta}</div>
      </div>
    );
  }
);

export default UpsellCard;
